import { useEffect, useLayoutEffect, useRef } from "react";
import { useNavigationType } from "react-router-dom";
import { useRouteTransition } from "@/components/RouteTransitionContext";

/**
 * Sincroniza a posição de rolagem com a transição de rota:
 *
 * - O gatilho é o `token` de `RouteTransitionContext` (incrementado
 *   em `useLayoutEffect` no commit). Como o `<PageTransition>` usa
 *   o mesmo token como `key` para reiniciar o fade, scroll e fade
 *   reagem ao MESMO commit — garantindo um único paint sincronizado
 *   mesmo em dispositivos lentos.
 *
 * - PUSH/REPLACE: rola para o topo, instantâneo.
 * - POP: restaura posição salva da rota anterior.
 *
 * `behavior: "auto"` para nunca exibir scroll animado competindo
 * com o fade.
 */
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

const ScrollRestoration = () => {
  const { routeKey, token } = useRouteTransition();
  const navType = useNavigationType(); // "PUSH" | "REPLACE" | "POP"
  const positionsRef = useRef<Map<string, number>>(new Map());
  const lastKeyRef = useRef<string | null>(null);

  // Mesmo commit em que o token muda → mesmo paint em que o fade
  // reinicia. Scroll é aplicado de forma síncrona, sem rAF.
  useIsomorphicLayoutEffect(() => {
    const prev = lastKeyRef.current;
    if (prev && prev !== routeKey) {
      positionsRef.current.set(prev, window.scrollY);
    }

    if (navType === "POP") {
      const saved = positionsRef.current.get(routeKey) ?? 0;
      window.scrollTo({ top: saved, left: 0, behavior: "auto" });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }

    lastKeyRef.current = routeKey;
  }, [token, routeKey, navType]);

  // Capturar scroll continuamente para a entrada ativa
  // (sem listeners pesados — leitura on-demand no unload).
  useEffect(() => {
    const save = () => {
      if (lastKeyRef.current) {
        positionsRef.current.set(lastKeyRef.current, window.scrollY);
      }
    };
    window.addEventListener("pagehide", save);
    return () => {
      save();
      window.removeEventListener("pagehide", save);
    };
  }, []);

  return null;
};

export default ScrollRestoration;

